import React, { useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { ChevronDown, Check, AlertCircle, RefreshCw, X } from 'lucide-react';
import API_ENDPOINTS from '../../../config/api';
import { useTranslation } from '../../../hooks/useTranslation';

const ScenarioGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(380px, 100%), 1fr));
  gap: 16px;
  max-width: 900px;
`;

const ScenarioCard = styled.div`
  background: linear-gradient(145deg, rgba(26, 26, 26, 0.8), rgba(34, 34, 34, 0.6));
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  padding: 20px;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(255, 255, 255, 0.15);
  }
`;

const ScenarioHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
`;

const ScenarioIcon = styled.div`
  width: 44px;
  height: 44px;
  background: ${p => p.$color || 'linear-gradient(135deg, #6366f1, #4f46e5)'};
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
`;

const ScenarioTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 2px 0;
`;

const ScenarioDescription = styled.p`
  font-size: 12px;
  color: #666;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FutureSelector = styled.div`
  position: relative;
`;

const SelectorLabel = styled.label`
  display: block;
  font-size: 11px;
  color: #888;
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const SelectorButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid ${p => p.$open ? '#6366f1' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 10px;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;

  &:hover { border-color: ${p => p.$open ? '#6366f1' : 'rgba(255, 255, 255, 0.2)'}; }

  svg {
    transition: transform 0.15s ease;
    transform: ${p => p.$open ? 'rotate(180deg)' : 'rotate(0deg)'};
    opacity: 0.6;
  }
`;

const SelectedValue = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CustomBadge = styled.span`
  font-size: 10px;
  padding: 2px 6px;
  background: rgba(255, 170, 0, 0.2);
  color: #ffaa00;
  border-radius: 4px;
  text-transform: uppercase;
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #1a1a1a;
  border: 1px solid #333;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  z-index: 100;
`;

const DropdownOption = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: ${p => p.$active ? 'rgba(99, 102, 241, 0.15)' : 'transparent'};
  border: none;
  color: #fff;
  font-size: 13px;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;

  &:hover { background: rgba(99, 102, 241, 0.1); }
  &:not(:last-child) { border-bottom: 1px solid rgba(255, 255, 255, 0.05); }
`;

const OptionLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NoFuturesMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: rgba(255, 170, 0, 0.1);
  border: 1px solid rgba(255, 170, 0, 0.2);
  border-radius: 10px;
  color: #ffaa00;
  font-size: 12px;
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: ${p => p.$saving ? '#ffaa00' : p.$saved ? '#22c55e' : '#666'};
  margin-top: 6px;

  svg {
    animation: ${p => p.$saving ? 'spin 1s linear infinite' : 'none'};
  }

  @keyframes spin { to { transform: rotate(360deg); } }
`;

// ─── Impact-path-toggle toolbar ────────────────────────────────────

// ─── Change-future modal ──────────────────────────────────────────

const ModalBackdrop = styled.div`
  position: fixed; inset: 0;
  background: rgba(0, 0, 0, 0.78);
  z-index: 10000;
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
`;

const ModalCard = styled.div`
  background: #1f1f29;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  max-width: 540px;
  width: 100%;
  padding: 24px;
  color: #ddd;
  position: relative;
  & h3 { margin: 0 0 8px; color: #fff; font-size: 18px; }
  & p  { margin: 0 0 16px; font-size: 14px; line-height: 1.5; }
`;

const ModalActions = styled.div`
  display: flex; gap: 10px; flex-wrap: wrap;
`;

const PrimaryBtn = styled.button`
  flex: 1 1 200px;
  padding: 12px 16px;
  background: #6366f1;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  &:hover { filter: brightness(1.1); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const DangerBtn = styled.button`
  flex: 1 1 200px;
  padding: 12px 16px;
  background: rgba(239, 68, 68, 0.18);
  color: #fca5a5;
  border: 1px solid rgba(239, 68, 68, 0.5);
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  &:hover { filter: brightness(1.15); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const TertiaryBtn = styled.button`
  position: absolute;
  top: 12px; right: 12px;
  background: transparent;
  border: none;
  color: #888;
  cursor: pointer;
  padding: 4px;
  &:hover { color: #fff; }
`;

const ModalError = styled.div`
  margin: 0 0 16px;
  color: #fca5a5;
  font-size: 13px;
`;

// Fill a translated template's {{name}} slots with React nodes (bold/italic
// fragments) so the word order stays the translation's own.
function fillNodes(template, nodes) {
  return template.split(/(\{\{\w+\}\})/).map((part, i) => {
    const m = part.match(/^\{\{(\w+)\}\}$/);
    return m ? <React.Fragment key={i}>{nodes[m[1]]}</React.Fragment> : part;
  });
}

const SCENARIO_ICONS = {
  'Demokratia': '🏛️', 'Ilmasto': '🌍', 'Ilmastonmuutos': '🌍',
  'Teknologia': '💻', 'Vaesto': '👥', 'Talous': '💰',
};

const SCENARIO_COLORS = {
  'Demokratia': 'linear-gradient(135deg, #6366f1, #4f46e5)',
  'Ilmasto': 'linear-gradient(135deg, #22c55e, #16a34a)',
  'Ilmastonmuutos': 'linear-gradient(135deg, #22c55e, #16a34a)',
  'Teknologia': 'linear-gradient(135deg, #0ea5e9, #0284c7)',
  'Vaesto': 'linear-gradient(135deg, #f59e0b, #d97706)',
  'Talous': 'linear-gradient(135deg, #ec4899, #db2777)',
};

/**
 * FutureSelectionContent - Scenario future variant selector.
 * Extracted from WorkshopControls.jsx for use inside PhaseSection.
 */
export default function FutureSelectionContent({ scenarios: propScenarios, instanceId, locked = false }) {
  const { t } = useTranslation();
  const [scenarios, setScenarios] = useState(propScenarios || []);
  const [scenarioFutures, setScenarioFutures] = useState({});
  const [openDropdown, setOpenDropdown] = useState(null);
  const [savingState, setSavingState] = useState({});
  const [loading, setLoading] = useState(!propScenarios);
  // Change-future modal: { scenarioId, scenarioTitle, newFuture, contentCount }
  // when non-null, presents the keep-or-wipe choice before the swap actually runs.
  const [changeModal, setChangeModal] = useState(null);
  const [modalBusy, setModalBusy] = useState(false);
  const [modalError, setModalError] = useState(null);

  // Fetch scenarios + futures if not provided via props
  useEffect(() => {
    if (propScenarios?.length > 0) {
      setScenarios(propScenarios);
    }
  }, [propScenarios]);

  useEffect(() => {
    if (!instanceId) return;

    const fetchData = async () => {
      try {
        const socketUrl = API_ENDPOINTS.API_ORIGIN();
        let scenariosList = scenarios;

        if (!propScenarios || propScenarios.length === 0) {
          const res = await fetch(`${socketUrl}/api/scenarios/${instanceId}`);
          const data = await res.json();
          scenariosList = (data.scenarios || data || []).filter(s => s.is_active !== false);
          setScenarios(scenariosList);
        }

        const futuresMap = {};
        for (const scenario of scenariosList) {
          const varsRes = await fetch(`${socketUrl}/api/scenarios/${scenario.id}/variables`);
          const varsData = await varsRes.json();
          const variablesList = varsData.variables || varsData || [];
          futuresMap[scenario.id] = [...new Set(
            variablesList.map(v => v.metadata?.tulevaisuuskuva).filter(Boolean)
          )];
        }
        setScenarioFutures(futuresMap);
      } catch (error) {
        console.error('Failed to fetch futures data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [instanceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Plain PATCH of selected_tulevaisuuskuva (no side-effects). Used when
  // there is no impact-path content yet for the scenario, OR after the user
  // explicitly chose "wipe" from the modal (the wipe endpoint cleared content
  // first, so a plain PATCH is now safe).
  const patchFuture = useCallback(async (scenarioId, future) => {
    setScenarios(prev => prev.map(s =>
      s.id === scenarioId ? { ...s, selected_tulevaisuuskuva: future } : s
    ));
    setSavingState(prev => ({ ...prev, [scenarioId]: 'saving' }));
    try {
      const socketUrl = API_ENDPOINTS.API_ORIGIN();
      const res = await fetch(`${socketUrl}/api/scenarios/${scenarioId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ selected_tulevaisuuskuva: future }),
      });
      if (!res.ok) throw new Error('Failed to save');
      setSavingState(prev => ({ ...prev, [scenarioId]: 'saved' }));
      setTimeout(() => setSavingState(prev => ({ ...prev, [scenarioId]: null })), 2000);
    } catch (error) {
      console.error('Failed to save future selection:', error);
      setSavingState(prev => ({ ...prev, [scenarioId]: null }));
    }
  }, []);

  // Count nodes for a scenario (instance-scoped). Returns 0 if the endpoint
  // doesn't return what we expect — we treat unknown as "no content," so the
  // worst case is we skip the modal and patch silently.
  const countScenarioContent = useCallback(async (scenarioId) => {
    try {
      const socketUrl = API_ENDPOINTS.API_ORIGIN();
      const res = await fetch(`${socketUrl}/api/pathways?kid=${encodeURIComponent(instanceId)}&scenarioId=${encodeURIComponent(scenarioId)}`);
      if (!res.ok) return 0;
      const j = await res.json();
      const all = Array.isArray(j) ? j : (j?.pathways || []);
      // Count only user content (depth >= 2). Depth 0/1 are scaffolding.
      return all.filter(n => (n.depth ?? 0) >= 2).length;
    } catch { return 0; }
  }, [instanceId]);

  const handleSelectFuture = useCallback(async (scenarioId, future) => {
    if (locked) { setOpenDropdown(null); return; }
    setOpenDropdown(null);

    const scenario = scenarios.find(s => s.id === scenarioId);
    const previousFuture = scenario?.selected_tulevaisuuskuva || null;
    // Pure no-op: same future already selected.
    if (previousFuture === future) return;

    // If there's no previous future OR the new one is "Oma" (null) on a fresh
    // scenario, there can't be any existing impact-path scaffolding to worry
    // about — patch directly.
    if (!previousFuture) {
      await patchFuture(scenarioId, future);
      return;
    }

    // Otherwise: changing from one future to another on a scenario that may
    // already have content. Check before we ask the facilitator anything;
    // no point showing a modal if the scenario is empty.
    const contentCount = await countScenarioContent(scenarioId);
    if (contentCount === 0) {
      await patchFuture(scenarioId, future);
      return;
    }

    // Existing content + future change → confirm via modal.
    setModalError(null);
    setChangeModal({
      scenarioId,
      scenarioTitle: scenario?.title || t('futureSelection.scenarioFallback'),
      previousFuture,
      newFuture: future,
      contentCount,
    });
  }, [locked, scenarios, patchFuture, countScenarioContent, t]);

  // Modal actions ─────────────────────────────────────────────────────

  const handleKeepContent = useCallback(async () => {
    if (!changeModal) return;
    setModalBusy(true);
    setModalError(null);
    try {
      const socketUrl = API_ENDPOINTS.API_ORIGIN();
      const res = await fetch(
        `${socketUrl}/api/scenarios/${changeModal.scenarioId}/swap-tulevaisuuskuva`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            instanceId,
            newTulevaisuuskuva: changeModal.newFuture,
          }),
        }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setScenarios(prev => prev.map(s =>
        s.id === changeModal.scenarioId
          ? { ...s, selected_tulevaisuuskuva: changeModal.newFuture }
          : s
      ));
      setChangeModal(null);
    } catch (err) {
      console.error('Swap failed:', err);
      setModalError(t('futureSelection.swapFailed', { error: err.message }));
    } finally {
      setModalBusy(false);
    }
  }, [changeModal, instanceId, t]);

  const handleWipeAndSwitch = useCallback(async () => {
    if (!changeModal) return;
    setModalBusy(true);
    setModalError(null);
    try {
      const socketUrl = API_ENDPOINTS.API_ORIGIN();
      const wipeRes = await fetch(
        `${socketUrl}/api/scenarios/${changeModal.scenarioId}/wipe-impact-path`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ instanceId }),
        }
      );
      if (!wipeRes.ok) throw new Error(`Wipe failed: HTTP ${wipeRes.status}`);
      await patchFuture(changeModal.scenarioId, changeModal.newFuture);
      setChangeModal(null);
    } catch (err) {
      console.error('Wipe-and-switch failed:', err);
      setModalError(t('futureSelection.wipeFailed', { error: err.message }));
    } finally {
      setModalBusy(false);
    }
  }, [changeModal, instanceId, patchFuture, t]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = () => setOpenDropdown(null);
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (loading) return <div style={{ color: '#666', padding: 24 }}>{t('futureSelection.loading')}</div>;
  if (scenarios.length === 0) return <div style={{ color: '#666', padding: 24 }}>{t('futureSelection.empty')}</div>;

  return (
    <>
      <ScenarioGrid>
      {scenarios.map(scenario => {
        const futures = scenarioFutures[scenario.id] || [];
        const hasFutures = futures.length > 0;
        const currentFuture = scenario.selected_tulevaisuuskuva;
        const isOpen = openDropdown === scenario.id;
        const saveState = savingState[scenario.id];

        const icon = Object.entries(SCENARIO_ICONS).find(
          ([key]) => scenario.title?.includes(key)
        )?.[1] || '📋';
        const color = Object.entries(SCENARIO_COLORS).find(
          ([key]) => scenario.title?.includes(key)
        )?.[1] || 'linear-gradient(135deg, #6366f1, #4f46e5)';

        return (
          <ScenarioCard key={scenario.id}>
            <ScenarioHeader>
              <ScenarioIcon $color={color}>{icon}</ScenarioIcon>
              <div style={{ flex: 1, minWidth: 0 }}>
                <ScenarioTitle>{scenario.title}</ScenarioTitle>
                <ScenarioDescription>
                  {scenario.description || t('futureSelection.futuresCount', { count: futures.length })}
                </ScenarioDescription>
              </div>
            </ScenarioHeader>

            {hasFutures ? (
              <FutureSelector>
                <SelectorLabel>{t('futureSelection.activeFuture')}</SelectorLabel>
                <SelectorButton
                  $open={isOpen}
                  onClick={(e) => { e.stopPropagation(); setOpenDropdown(isOpen ? null : scenario.id); }}
                >
                  <SelectedValue>
                    {currentFuture || t('futureSelection.customFuture')}
                    {!currentFuture && <CustomBadge>{t('futureSelection.freeBadge')}</CustomBadge>}
                  </SelectedValue>
                  <ChevronDown size={16} />
                </SelectorButton>

                {isOpen && (
                  <Dropdown onMouseDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()}>
                    <DropdownOption $active={!currentFuture} onClick={() => handleSelectFuture(scenario.id, null)}>
                      <OptionLabel>{t('futureSelection.customFuture')} <CustomBadge>{t('futureSelection.freeBadge')}</CustomBadge></OptionLabel>
                      {!currentFuture && <Check size={16} style={{ color: '#6366f1' }} />}
                    </DropdownOption>
                    {futures.map(future => (
                      <DropdownOption key={future} $active={currentFuture === future} onClick={() => handleSelectFuture(scenario.id, future)}>
                        <OptionLabel>{future}</OptionLabel>
                        {currentFuture === future && <Check size={16} style={{ color: '#6366f1' }} />}
                      </DropdownOption>
                    ))}
                  </Dropdown>
                )}

                {saveState && (
                  <StatusIndicator $saving={saveState === 'saving'} $saved={saveState === 'saved'}>
                    {saveState === 'saving' ? <><RefreshCw size={12} /> {t('futureSelection.saving')}</> : <><Check size={12} /> {t('futureSelection.saved')}</>}
                  </StatusIndicator>
                )}
              </FutureSelector>
            ) : (
              <NoFuturesMessage>
                <AlertCircle size={16} />
                {t('futureSelection.noFutures')}
              </NoFuturesMessage>
            )}
          </ScenarioCard>
        );
      })}
      </ScenarioGrid>

      {/* Change-future confirmation modal. Default action keeps participants'
          impact-path content and only swaps the future + variables, so a
          mid-workshop fix is minimally disruptive. The wipe button is the
          loud secondary action — destructive, opt-in. */}
      {changeModal && (
        <ModalBackdrop onClick={() => !modalBusy && setChangeModal(null)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <TertiaryBtn onClick={() => !modalBusy && setChangeModal(null)} aria-label={t('common.close')}>
              <X size={18} />
            </TertiaryBtn>
            <h3>{t('futureSelection.modalTitle')}</h3>
            <p>
              {fillNodes(t('futureSelection.modalExisting'), {
                scenario: <strong>{changeModal.scenarioTitle}</strong>,
                count: <strong>{changeModal.contentCount}</strong>,
                previous: <em>{changeModal.previousFuture}</em>,
              })}
            </p>
            <p>
              {fillNodes(t('futureSelection.modalRecommendation'), {
                next: <em>{changeModal.newFuture}</em>,
              })}
            </p>
            {modalError && <ModalError role="alert">{modalError}</ModalError>}
            <ModalActions>
              <PrimaryBtn onClick={handleKeepContent} disabled={modalBusy} autoFocus>
                {modalBusy ? t('futureSelection.switching') : t('futureSelection.keepContent')}
              </PrimaryBtn>
              <DangerBtn onClick={handleWipeAndSwitch} disabled={modalBusy}>
                {t('futureSelection.wipeAndRestart')}
              </DangerBtn>
            </ModalActions>
          </ModalCard>
        </ModalBackdrop>
      )}
    </>
  );
}
